import { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

import { ReqDatetime, ReqPagination, ResPagination, Timestamp } from '../../types';
import { TradeMode } from '../../v2/trade/types';
import { SocialProfile } from '../user/types';

export type CopyTradeStrategyMode = 'COPY_ALL_EXISTING' | 'COPY_BETTER_ENTRIES' | 'COPY_NEW_POSITIONS_ONLY';
export const copyTradeStrategyModes: CopyTradeStrategyMode[] = [
  'COPY_ALL_EXISTING',
  'COPY_BETTER_ENTRIES',
  'COPY_NEW_POSITIONS_ONLY',
];

export type CopyExecutionMode = 'FIXED_RATIO' | 'FIXED_MULTIPLIER';

export interface PostCopyTradePortfolioCopyRequest {
  initialFundAmountUsdt: string;
  initialCopyMode: CopyTradeStrategyMode;
  maximumLossRatioPct: string;
  maxEntrySlippagePct: string;
  /** Copy execution mode. Defaults to FIXED_RATIO if not provided. */
  copyExecutionMode?: CopyExecutionMode | null;
  /** Multiplier value. Required when copyExecutionMode is FIXED_MULTIPLIER. Ignored otherwise. */
  multiplier?: string | null;
}

export interface GetCopyTradeMeResponse {
  isMaster: boolean;
  totalCopyingBalanceInUsdt: string;
  totalInitialCopyingBalanceInUsdt: string;
  availableBalanceUsdt: string;
  totalOngoingCopiesUnrealizedPnlInUsdt: string;
  totalOngoingCopiesPnlInUsdt: string;
  totalOngoingCopiesPnlPct: string;
  totalStoppedCopiesRealizedPnlInUsdt: string;
  totalStoppedCopiesPnlInUsdt: string;
  totalStoppedCopiesPnlPct: string;
  numStoppedCopies: number;
  numOngoingCopies: number;
  maxEarnAprPct: string;
}

export interface GetCopyTradePortfoliosResponse {
  portfolios: CopyTradePortfolio[];
}

export type CopyTradePortfolioAvailabilityStatus =
  | 'AVAILABLE'
  | 'COPYING_IN_PROGRESS'
  | 'FULL_BY_COPIER_COUNT'
  | 'FULL_BY_INVESTMENT_AMOUNT';

export type CopyTradePerformancePeriod = '7d' | '30d' | '90d';

/** Performance KPIs of a master portfolio for a single time window (7D / 30D / 90D). */
export interface CopyTradePeriodPerformance {
  roiPct: string;
  pnlUsdt: string;
  /** Null when there are no closed positions. */
  winRatePct: string | null;
  copiersPnlUsdt: string;
  maxDrawdownPct: string;
  /** Null when there is insufficient data. */
  sharpeRatio: string | null;
  highestPnlUsdt: string;
  turnoverRatePct: string;
  pnlHistory: CopyTradePortfolioPnlHistory[];
}

export interface CopyTradePortfolio {
  portfolioId: string;
  userProfileId: string;
  nickname: string;
  /** The master's UID. Null if the master has no UID assigned. Searchable. */
  uid?: string | null;
  /** The master's own referral code. Null if the master has no referral code. Searchable. */
  referralCode?: string | null;
  isOwner: boolean;
  profileIconUrl?: string;
  currentCopierCount: number;
  maxCopierCount: number;
  /** Point-in-time assets under management (AUM) of the portfolio in USDT. */
  aumUsdt: string;
  /** Performance KPIs over the last 7 days. Null when the master has no activity in this window. */
  last7DaysPerformance?: CopyTradePeriodPerformance | null;
  /** Performance KPIs over the last 30 days. Null when the master has no activity in this window. */
  last30DaysPerformance?: CopyTradePeriodPerformance | null;
  /** Performance KPIs over the last 90 days. Null when the master has no activity in this window. */
  last90DaysPerformance?: CopyTradePeriodPerformance | null;
  availabilityStatus: CopyTradePortfolioAvailabilityStatus;
  preferredSymbols: string[];
  preferredLeverage: number;
  /**
   * @deprecated Use last30DaysPerformance.pnlHistory instead.
   */
  recentPnlHistory: CopyTradePortfolioPnlHistory[];
  profitSharingPct: string;
  availableInvestmentAmountUsdt: string;
  minInvestAmountUsdt: string;
  maxInvestAmountUsdt: string;
  /**
   * @deprecated Use last30DaysPerformance.roiPct instead.
   */
  recentTotalRoiPct: string;
  /**
   * @deprecated Use last30DaysPerformance.pnlUsdt instead.
   */
  recentTotalPnlUsdt: string;
  /**
   * @deprecated Use last30DaysPerformance.winRatePct instead.
   */
  winRatePct: string;
  /**
   * @deprecated Use last30DaysPerformance.highestPnlUsdt instead.
   */
  highestPnlUsdt: string;
  /**
   * @deprecated Use last30DaysPerformance.turnoverRatePct instead.
   */
  turnoverRatePct: string;
  interviewHeadline: string;
  interviewPostUrl: string;
  aiInsightsText: string;
  /** Minimum allowed multiplier value for fixed multiplier mode */
  multiplierMin: string;
  /** Maximum allowed multiplier value for fixed multiplier mode */
  multiplierMax: string;
  socialProfiles: SocialProfile[];
  hasAccess: boolean;
}

export const copyTradePerformancePeriodField = {
  '7d': 'last7DaysPerformance',
  '30d': 'last30DaysPerformance',
  '90d': 'last90DaysPerformance',
} as const satisfies Record<CopyTradePerformancePeriod, keyof CopyTradePortfolio>;

export interface CopyTradePortfolioPnlHistory {
  date: string;
  pnlUsdt: string;
  roiPct?: string;
}

export interface GetCopyTradePortfolioCopiersResponse extends ResPagination {
  copiers: CopyTradePortfolioCopier[];
}
export interface CopyTradePortfolioCopier {
  rank: number;
  userProfileId: string;
  nickname: string;
  currentFundInUsdt: string;
  currentTotalPnlInUsdt: string;
  currentTotalRoiPct: string;
  durationDays: number;
  vipTier: number;
  weeklyLeaderboardRank: number;
}

// history
export interface CopyTradeClosedPosition {
  positionId: string;
  symbol: string;
  side: V2OrderSideType;
  avgOpenPrice: string;
  avgClosePrice: string;
  qty: string;
  fundingFee: string;
  fundingFeeAqxpRedeemed?: string;
  tradingFee: string;
  tradingFeeAqxpRedeemed?: string;
  tradingFeeVoucherRedeemed?: string;
  grossRealizedPnlInUsdt: string;
  netRealizedPnlInUsdt: string;
  netRealizedPnlPct: string;
  openTime: Timestamp;
  closeTime: Timestamp;
  marginType: OrderMarginType;
  initLeverage: number;
}

export type CopyTradeCopyStatus = 'COPYING' | 'STOPPED' | 'PENDING_STOP';

export type CopyTradeMyCopyDetail =
  | CopyTradeMyOngoingCopyDetail
  | CopyTradeMyStoppedCopyDetail
  | CopyTradeMyPendingStopCopyDetail;
interface CopyTradeMyCopyBaseDetail {
  portfolioCopyId: string;
  portfolioId: string;
  /** Sub-account ID associated with this copy */
  subAccountId: number;
  masterNickname: string;
  masterProfileIconUrl?: string;
  masterUserProfileId: string;
  startedCopyingAt: string;
  realizedPnlInUsdt: string;
  profitSharingPct: string;
  profitSharedInUsdt: string;
  unrealizedPnlInUsdt: string;
  copyingBalanceInUsdt: string;
  stopLossTriggerBalanceInUsdt: string;
  initialFundAmountInUsdt: string;
  tradingVolume30dInUsdt: string | null;
  totalTradingVolumeInUsdt: string | null;
  netProfitInUsdt: string;
  totalPnlInUsdt: string;
  totalPnlPct: string;
  minTotalInvestedFundPerCopyInUsdt: string;
  maxTotalInvestedFundPerCopyInUsdt: string;
  /** Copy execution mode - FIXED_RATIO or FIXED_MULTIPLIER */
  copyExecutionMode: CopyExecutionMode;
  /** Multiplier value for fixed multiplier mode. Null if mode is FIXED_RATIO. */
  multiplier: string | null;
  /** Trade mode of the sub-account (MultiplePositions or OneWay) */
  subAccountTradeMode: TradeMode;
  /** Symbols that this copy instance actively holds positions. Null for stopped copies. */
  openPositionSymbols: string[] | null;
}
export interface CopyTradeMyOngoingCopyDetail extends CopyTradeMyCopyBaseDetail {
  status: 'COPYING';
  stoppedCopyingAt: undefined;
  marketOpenAt: undefined;
}
export interface CopyTradeMyStoppedCopyDetail extends CopyTradeMyCopyBaseDetail {
  status: 'STOPPED';
  stoppedCopyingAt: string;
  marketOpenAt: undefined;
}
export interface CopyTradeMyPendingStopCopyDetail extends CopyTradeMyCopyBaseDetail {
  status: 'PENDING_STOP';
  stoppedCopyingAt: undefined;
  /** Timestamp when the market is expected to open, in nanoseconds since epoch. */
  marketOpenAt: string;
}

export interface CopyTradeMyCopyStopEstimation {
  initialFundAmountInUsdt: string;
  finalPnlInUsdt: string;
  estimatedTotalProfitShareInUsdt: string;
  estimatedTotalReceivingAmountInUsdt: string;
}

export interface CopyTradeMyCopyPosition {
  positionId: string;
  symbol: string;
  side: V2OrderSideType;
  marginType: OrderMarginType;
  initLeverage: number;
  qty: string;
  avgOpenPrice: string;
  liquidationPrice: string;
  unrealizedPnlPct: string;
  unrealizedPnlInUsdt: string;
  margin: string;
  openTime: string;
}

export type CopyTradeProfitShareType = 'WEEKLY' | 'COPY_STOP' | 'REDUCE_BALANCE';

export interface CopyTradeProfitShared {
  profitSharedId: string;
  sharedAt: Timestamp;
  shareType: CopyTradeProfitShareType;
  totalPnlInUsdt: string;
  profitSharedInUsdt: string;
}

export interface CopyTradeFundingHistory {
  fundingHistoryId: string;
  symbol: string;
  side: V2OrderSideType;
  fundingFeeInUsdt: string;
  fundingRatePct: string;
  transactTime: string;
}

export interface CopyTradeFailedCopyOrder {
  failedCopyOrderId: string;
  side: V2OrderSideType;
  symbol: string;
  availableBalanceInUsdt: string;
  failedReason: CopyTradeFailedReason;
  failedAt: Timestamp;
}
export type CopyTradeFailedReason =
  | 'INSUFFICIENT_FUNDS'
  | 'BELOW_MINIMUM_ORDER_AMOUNT'
  | 'SLIPPAGE_LIMIT_EXCEEDED'
  | 'ORDER_AMOUNT_LIMIT_EXCEEDED'
  | 'COPY_ORDER_TIMEOUT'
  | 'BAD_MARKET_CONDITIONS'
  | 'SYSTEM_ERROR';

export interface GetCopyTradeFailedOrdersResponse extends ResPagination {
  failedOrders: CopyTradeFailedCopyOrder[];
}

export interface GetCopyTradeHistoryResponse extends ResPagination {
  history: CopyTradeClosedPosition[];
}

export interface GetCopyTradeMyCopyPositionsResponse extends ResPagination {
  positions: CopyTradeMyCopyPosition[];
}

export interface GetCopyTradeProfitSharedResponse extends ResPagination {
  history: CopyTradeProfitShared[];
}

export interface GetCopyTradeFundingHistoryResponse extends ResPagination {
  history: CopyTradeFundingHistory[];
}

export interface GetCopyTradeMyCopiesResponse extends ResPagination {
  copies: CopyTradeMyCopyDetail[];
}

// Transfer types
export type CopyTradeTransferType = 'DEPOSIT_FUND' | 'WITHDRAW_FUND' | 'PROFIT_SHARE';

export interface CopyTradeTransfer {
  transferId: string;
  portfolioCopyId: string;
  type: CopyTradeTransferType;
  amount: string;
  currency: string;
  masterNickname: string;
  transferredAt: Timestamp;
}

export interface GetCopyTradeTransfersParams extends ReqPagination, ReqDatetime {}

export interface GetCopyTradeTransfersResponse extends ResPagination {
  transfers: CopyTradeTransfer[];
}

// Balance request/response types
export interface PostCopyTradeAddBalanceRequest {
  currency: string;
  amount: string;
}

export interface PostCopyTradeAddBalanceResponse {
  portfolioCopyId: string;
  currency: string;
  amount: string;
}

export interface PostCopyTradeReduceBalanceRequest {
  currency: string;
  amount: string;
}

export interface PostCopyTradeReduceBalanceResponse {
  portfolioCopyId: string;
  currency: string;
  amount: string;
}

export interface GetCopyTradeEstimatedProfitShareResponse {
  estimatedProfitShareInUsdt: string;
}

export interface CopyTradeSummaryTopMaster {
  masterPortfolioId: string;
  userProfileId: string;
  nickname: string;
  profileIconUrl?: string;
  recentTotalRoiPct: string;
  recentTotalPnlInUsdt: string;
}

export interface GetCopyTradeSummaryResponse {
  topMaster: CopyTradeSummaryTopMaster;
}

// Apply master trader
export interface PostCopyTradeApplyMasterRequest {
  name: string;
  socialMediaLink: string;
  profitSharePct: string;
  volumeRebatePct: string;
  invitationCode: string | null;
}

// Portfolio setup
export type ProfitShareChangeRequestStatus = 'UNDER_REVIEW' | 'ACCEPTED' | 'REJECTED';

export interface ProfitShareChangeRequest {
  status: ProfitShareChangeRequestStatus;
  profitSharePctFrom: string;
  profitSharePctTo: string;
  finalizedAt: string | null;
}

export interface GetCopyTradePortfolioSetupResponse {
  profitSharePct: string;
  recentProfitShareChangeRequest: ProfitShareChangeRequest | null;
  volumeRebatePct: string;
  invitationCode: string | null;
}

export interface PostCopyTradePortfolioSetupRequest {
  profitSharePct?: string | null;
  invitationCode?: string | null;
}

export interface PostCopyTradePortfolioCopyResponse {
  portfolioCopyId: string;
  /** Sub-account ID assigned for this copy */
  subAccountId: number;
}
